//External libs
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import {ApiArrayModel} from 'src/app/common/model';
import CultureSummaryReportModel from './culture-summary-report.model';

/**
 * @service CultureSummaryReportService
 * @description Service for retrieving culture summary reports.
 * Provides methods to fetch aggregated culture data for business entities and farm sites.
 * Extends base Service class with generic type CultureSummaryReportModel.
 *
 * @feature 1. Fetch culture summary reports with role-based filtering
 * @feature 2. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<CultureSummaryReportModel>. Handles report generation logic
 * where payload is adjusted based on the logged-in user's role (EMPLOYEE vs others).
 * Uses HttpClient for HTTP operations.
 *
 * @service-endpoint /blubay/culturesummary/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class CultureSummaryReportService extends Service<CultureSummaryReportModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/culturesummary/');
  }

  /**
   * @method getCultureSummaryReport
   * @async
   * @description Fetch culture summary report data based on business and farm site
   *
   * Logic:
   * - Constructs payload with business_id and farm_site_id.
   * - Checks `localStorage.loginPersonType`.
   * - If user is NOT 'EMPLOYEE', removes `business_id` from payload (backend likely infers it from token or context).
   * - Makes HTTP POST request to retrieve report data.
   *
   * @param {string} businessID - ID of the business entity (used for EMPLOYEE role)
   * @param {any} farmsitesId - ID of the farm site to filter report
   * @returns {Promise<Array<CultureSummaryReportModel>>} Promise resolving to array of report models
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getCultureSummaryReport(businessID: string, farmsitesId: any) {
    let payload = { business_id: businessID, farm_site_id: farmsitesId }
    if (localStorage.loginPersonType != 'EMPLOYEE') {
      delete payload.business_id;
    }
    return new Promise<Array<CultureSummaryReportModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<CultureSummaryReportModel>>(`${this.baseUrl}${this.serviceUrl}`, payload, this.httpOptions).subscribe(response => {
        resolve(response.data);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
